import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChartBarIcon,
  TrophyIcon,
  ShoppingBagIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  ArrowTrendingUpIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const AgentPerformancePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedAgent, setSelectedAgent] = useState(id || '');
  const [dateRange, setDateRange] = useState('month');

  // Fetch agents list
  const { data: agentsData } = useQuery({
    queryKey: ['field-agents-list'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/admin/field-agents', {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 1000 }
      });
      return data.agents;
    }
  });

  // Fetch overall performance (analytics)
  const { data: overallData, isLoading: overallLoading } = useQuery({
    queryKey: ['agent-performance-overall', dateRange],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/admin/agent-performance/analytics', {
        headers: { Authorization: `Bearer ${token}` },
        params: { period: dateRange }
      });
      // Remap analytics response to match UI's expected summary shape
      return {
        ...data,
        summary: {
          total_revenue: data.orders?.revenue || 0,
          total_orders: data.orders?.total || 0,
          avg_order_value: data.orders?.avg_order_value || 0,
          active_companies: data.assignments?.total || 0
        }
      };
    },
    enabled: !selectedAgent
  });

  // Fetch individual agent performance
  const { data: agentData, isLoading: agentLoading } = useQuery({
    queryKey: ['agent-performance', selectedAgent, dateRange],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`/api/admin/agent-performance/${selectedAgent}/performance`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { period: dateRange }
      });
      // Remap response to match UI expectations
      return {
        ...data,
        summary: {
          ...data.summary,
          active_companies: data.summary?.assigned_companies || 0
        },
        targets: (data.current_targets || []).map(t => ({
          ...t,
          achieved_value: t.progress?.current || t.achieved_value || 0
        })),
        top_companies: (data.top_companies || []).map(c => ({
          company_id: c.company?.id || c.company_id,
          company_name: c.company?.name || c.company_name,
          order_count: c.orders_count || c.order_count || 0,
          total_revenue: parseFloat(c.total_revenue) || 0
        }))
      };
    },
    enabled: !!selectedAgent
  });

  // Fetch leaderboard
  const { data: leaderboardData } = useQuery({
    queryKey: ['agent-leaderboard', dateRange],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/admin/agent-performance/leaderboard', {
        headers: { Authorization: `Bearer ${token}` },
        params: { period: dateRange }
      });
      // Remap leaderboard entries to flat shape expected by UI
      return {
        ...data,
        leaderboard: (data.leaderboard || []).map(entry => ({
          agent_id: entry.agent?.id,
          agent_name: `${entry.agent?.user?.first_name || ''} ${entry.agent?.user?.last_name || ''}`.trim(),
          employee_id: entry.agent?.employee_id,
          total_revenue: entry.stats?.total_revenue || 0,
          total_orders: entry.stats?.total_orders || 0
        }))
      };
    }
  });

  const isLoading = selectedAgent ? agentLoading : overallLoading;
  const performanceData = selectedAgent ? agentData : overallData;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">מעקב ביצועים</h1>
          <p className="text-gray-600 mt-1">ניתוח ביצועי סוכני שטח</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">בחר סוכן</label>
            <select
              value={selectedAgent}
              onChange={(e) => {
                setSelectedAgent(e.target.value);
                if (e.target.value) {
                  navigate(`/admin/agent-performance/${e.target.value}`);
                } else {
                  navigate('/admin/agent-performance');
                }
              }}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">כל הסוכנים - סקירה כללית</option>
              {agentsData?.map(agent => (
                <option key={agent.id} value={agent.id}>
                  {agent.user?.first_name} {agent.user?.last_name} ({agent.employee_id})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">טווח תאריכים</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="week">שבוע אחרון</option>
              <option value="month">חודש אחרון</option>
              <option value="quarter">רבעון אחרון</option>
              <option value="year">שנה אחרונה</option>
            </select>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">טוען נתונים...</p>
        </div>
      ) : (
        <>
          {/* Performance Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <CurrencyDollarIcon className="w-6 h-6 text-green-600" />
                </div>
                <div className="mr-4">
                  <p className="text-sm text-gray-600">סך הכנסות</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ₪{performanceData?.summary?.total_revenue?.toLocaleString() || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <ShoppingBagIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div className="mr-4">
                  <p className="text-sm text-gray-600">סך הזמנות</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {performanceData?.summary?.total_orders || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <BuildingOfficeIcon className="w-6 h-6 text-purple-600" />
                </div>
                <div className="mr-4">
                  <p className="text-sm text-gray-600">חברות פעילות</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {performanceData?.summary?.active_companies || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <ArrowTrendingUpIcon className="w-6 h-6 text-orange-600" />
                </div>
                <div className="mr-4">
                  <p className="text-sm text-gray-600">ממוצע הזמנה</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ₪{performanceData?.summary?.avg_order_value?.toLocaleString() || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Leaderboard */}
          {!selectedAgent && leaderboardData?.leaderboard && (
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <TrophyIcon className="w-6 h-6 text-yellow-500" />
                  טבלת מובילים
                </h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {leaderboardData.leaderboard.map((agent, index) => (
                    <div
                      key={agent.agent_id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      onClick={() => {
                        setSelectedAgent(agent.agent_id);
                        navigate(`/admin/agent-performance/${agent.agent_id}`);
                      }}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          index === 0 ? 'bg-yellow-500 text-white' :
                          index === 1 ? 'bg-gray-400 text-white' :
                          index === 2 ? 'bg-orange-500 text-white' :
                          'bg-gray-200 text-gray-700'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {agent.agent_name}
                          </div>
                          <div className="text-sm text-gray-500">{agent.employee_id}</div>
                        </div>
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-gray-900">
                          ₪{agent.total_revenue?.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-500">
                          {agent.total_orders} הזמנות
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Individual Agent Details */}
          {selectedAgent && agentData && (
            <>
              {/* Targets Progress */}
              {agentData.targets && agentData.targets.length > 0 && (
                <div className="bg-white rounded-lg shadow mb-6">
                  <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900">יעדים והתקדמות</h2>
                  </div>
                  <div className="p-6">
                    <div className="space-y-4">
                      {agentData.targets.map((target) => {
                        const progress = target.target_value ? (target.achieved_value / target.target_value * 100) : 0;
                        return (
                          <div key={target.id} className="border-b border-gray-200 pb-4 last:border-b-0">
                            <div className="flex justify-between items-center mb-2">
                              <div>
                                <span className="font-medium text-gray-900">
                                  {target.target_type === 'revenue' ? 'יעד הכנסות' : 
                                   target.target_type === 'orders' ? 'יעד הזמנות' : 
                                   'יעד לקוחות חדשים'}
                                </span>
                                <span className="text-sm text-gray-500 mr-2">
                                  {new Date(target.period_start).toLocaleDateString('he-IL')} - 
                                  {new Date(target.period_end).toLocaleDateString('he-IL')}
                                </span>
                              </div>
                              <span className="text-sm font-medium text-gray-700">
                                {Math.min(progress, 100).toFixed(1)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div
                                className={`h-3 rounded-full transition-all ${
                                  progress >= 100 ? 'bg-green-600' :
                                  progress >= 75 ? 'bg-blue-600' :
                                  progress >= 50 ? 'bg-yellow-600' :
                                  'bg-red-600'
                                }`}
                                style={{ width: `${Math.min(progress, 100)}%` }}
                              />
                            </div>
                            <div className="flex justify-between mt-1 text-sm text-gray-600">
                              <span>
                                {target.target_type === 'revenue' ? 
                                  `₪${target.achieved_value?.toLocaleString() || 0}` : 
                                  target.achieved_value || 0}
                              </span>
                              <span>
                                {target.target_type === 'revenue' ? 
                                  `₪${target.target_value?.toLocaleString()}` : 
                                  target.target_value}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Top Companies */}
              {agentData.top_companies && agentData.top_companies.length > 0 && (
                <div className="bg-white rounded-lg shadow">
                  <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900">חברות מובילות</h2>
                  </div>
                  <div className="p-6">
                    <div className="space-y-3">
                      {agentData.top_companies.map((company) => (
                        <div key={company.company_id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div>
                            <div className="font-medium text-gray-900">{company.company_name}</div>
                            <div className="text-sm text-gray-500">{company.order_count} הזמנות</div>
                          </div>
                          <div className="text-left">
                            <div className="font-bold text-gray-900">
                              ₪{company.total_revenue?.toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Empty State */}
          {!isLoading && !performanceData && (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <ChartBarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">אין נתוני ביצועים</h3>
              <p className="text-gray-600">לא נמצאו נתונים לתקופה שנבחרה</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AgentPerformancePage;


